import EmployeeAuditEntry from "../services/model/EmployeeAuditEntry";

export const FIELD_OPTIONS: { value: string; label: string }[] = [
  { value: "first_name", label: "First name" },
  { value: "last_name", label: "Last name" },
  { value: "mobile_number", label: "Mobile number" },
  { value: "smartwage_status", label: "Status" },
  { value: "bank_account_number", label: "Bank account" },
  { value: "metadata", label: "Metadata" },
];

export const SOURCE_OPTIONS: {
  value: "system" | "manual" | "integration";
  label: string;
}[] = [
  { value: "manual", label: "Manual" },
  { value: "system", label: "System" },
  { value: "integration", label: "Integration" },
];

const FIELD_LABELS: Record<string, string> = {
  first_name: "First name",
  last_name: "Last name",
  mobile_number: "Mobile number",
  email: "Email",
  id_number: "ID number",
  employee_no: "Employee number",
  salary: "Salary",
  start_of_employment_date: "Start date",
  pay_cycle: "Pay cycle",
  branch: "Branch",
  division: "Division",
  bank_name: "Bank name",
  bank_branch_name: "Bank branch name",
  bank_branch_code: "Bank branch code",
  bank_account_number: "Bank account",
  registration_date: "Registration date",
  term_cond: "Terms & conditions",
  metadata: "Metadata",
  smartwage_status: "Status",
  status_reason: "Status reason",
  black_listed: "Blacklisted",
  blacklist_reason: "Blacklist reason",
  risky: "Risky",
  risky_reasons: "Risky reasons",
  risk_metric: "Risk metric",
};

export const formatFieldLabel = (field: string): string => {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  if (field.startsWith("metadata.")) {
    const key = field.slice("metadata.".length);
    return key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
  }
  return field;
};

export const formatAuditValue = (
  value: string | number | boolean | null
): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const escapeCSVField = (field: string): string => {
  const sanitized = /^[\t\r ]*[=+\-@]/.test(field) ? `'${field}` : field;
  if (
    sanitized.includes(",") ||
    sanitized.includes("\n") ||
    sanitized.includes("\r") ||
    sanitized.includes('"')
  ) {
    return `"${sanitized.replace(/"/g, '""')}"`;
  }
  return sanitized;
};

export const formatSource = (entry: EmployeeAuditEntry): string => {
  if (entry.source === "manual") return "Manual";
  if (entry.source === "system") return "System";
  if (entry.source === "integration") return "Integration";
  return "System";
};

export const generateAuditLogCSV = (entries: EmployeeAuditEntry[]): string => {
  const headers = ["Date", "Type", "Source", "Field", "Old value", "New value"];
  const rows = [headers.join(",")];

  entries.forEach((entry) => {
    const date = entry.date_changed
      ? new Date(entry.date_changed).toLocaleString()
      : "";

    rows.push(
      [
        escapeCSVField(date),
        escapeCSVField(entry.change_type),
        escapeCSVField(formatSource(entry)),
        escapeCSVField(formatFieldLabel(entry.field)),
        escapeCSVField(formatAuditValue(entry.old_value)),
        escapeCSVField(formatAuditValue(entry.new_value)),
      ].join(",")
    );
  });

  return rows.join("\n");
};

export const downloadAuditLogCSV = (
  content: string,
  filename: string
): void => {
  const blob = new Blob([`\ufeff${content}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
